import { useEffect } from 'react';

import { useMap } from '../hooks/useMap';
import { useMapStyle } from '../hooks/useMapStyle';
import { MeasurementUnits, PoiCategories, PoiTypes } from '../types';
import {
  getThriftRouteTypeFromGraphQlRouteType,
  reloadLayerSource
} from '../utils';

export interface PointsOfInterestProps {
  /**
   * POI types to show.
   */
  poiTypes?: PoiTypes;

  /**
   * POIs to show based on the route type.
   */
  routeType?: number | string | null;

  /**
   * Whether to include route count in the POI title.
   */
  includeRouteCount?: boolean;

  /**
   * Measurement units to use for elevation in peak POIs.
   */
  measurementUnits?: MeasurementUnits;

  /**
   * Whether the layer is visible or not.
   */
  visible?: boolean;

  /**
   * Array of POI categories to hide from the map. Categories are more granular than
   * poiTypes. For example, the poiType "static" includes categories like "peak" and
   * "camp-site" which could be filtered out individually.
   */
  categoryFilter?: PoiCategories;
}

export const PointsOfInterest = ({
  measurementUnits = 'metric',
  poiTypes = ['static'],
  routeType: routeTypeProp = null,
  includeRouteCount = false,
  visible = false,
  categoryFilter = []
}: PointsOfInterestProps) => {
  const map = useMap();
  const mapStyle = useMapStyle();
  const sourceName = 'pois';
  const layerName = 'pois';
  const visibility = visible ? 'visible' : 'none';
  const poiCategoryGroups = poiTypes ? `${poiTypes.join(',')}` : null;
  const routeType =
    typeof routeTypeProp === 'string'
      ? getThriftRouteTypeFromGraphQlRouteType(routeTypeProp)
      : routeTypeProp;

  useEffect(() => {
    if (map?.getSource(sourceName) && visible) {
      const params = new URLSearchParams({
        include_route_count: includeRouteCount.toString()
      });

      if (routeType) {
        params.append(`route_type`, routeType.toString());
      }

      if (poiCategoryGroups) {
        params.append(`poi_category_groups`, poiCategoryGroups);
      }

      if (categoryFilter.length) {
        map.setFilter(layerName, [
          '!',
          ['in', ['get', 'category'], ['literal', categoryFilter]]
        ]);
      }

      reloadLayerSource(map, sourceName, `?${params.toString()}`);
    }
  }, [map, mapStyle, visible, routeType, includeRouteCount, poiCategoryGroups]);

  useEffect(() => {
    if (map?.getLayer(layerName)) {
      // Server side will return `elevation` in units in the map style based on users unit
      // preference, however when logged out it will default to metric.
      //
      // In some cases we want to change the default for logged out on the client side as
      // our server side does not have a good way yet on figuring out where user might be
      // viewing the page and provide smarter defaults, ex: when viewing from US use
      // imperial
      //
      // There is also a use case like web route builder where the user can change unit
      // system for that page only while building a route and we would want to update peak
      // POIs units to respect the setting.
      //
      // NOTE: this code grabs layout property that is being added by the server and
      // modifies it, so there is a chance that server/client can get out of sync
      //
      const textField = map.getLayoutProperty(layerName, 'text-field');
      const peakIndex = textField.findIndex(
        (value: string[]) => value.toString() === 'peak'
      );

      if (peakIndex !== -1) {
        // we are using `elevation_ft` here as the value is guaranteed that it will be in
        // feet, however there is also `elevation` available which will change between
        // metric/imperial based on the server response so we can not use it here
        //
        // The following syntax is specific to mapbox-gl and called "expressions" Docs:
        // https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/
        //
        textField[peakIndex + 1] = [
          'case',
          ['has', 'elevation_ft'],
          [
            'case',
            ['!=', ['get', 'elevation_ft'], 0],
            [
              'concat',
              [
                'to-string',
                [
                  'round',
                  [
                    '*',
                    ['get', 'elevation_ft'],
                    measurementUnits === 'imperial' ? 1 : 0.3048
                  ]
                ]
              ],
              measurementUnits === 'imperial' ? 'ft\n' : 'm\n',
              ['get', 'title']
            ],
            ''
          ],
          ['get', 'title']
        ];

        map.setLayoutProperty(layerName, 'text-field', textField);
      }
    }
  }, [map, mapStyle, measurementUnits]);

  useEffect(() => {
    if (map?.getLayer(layerName)) {
      map.setLayoutProperty(layerName, 'visibility', visibility);
    }

    return () => {
      // The Map interface does not have a style property, but the Mapbox GL JS map
      // object does.
      // @ts-expect-error
      if (map && Boolean(map.style) && map.getLayer(layerName)) {
        map.setLayoutProperty(layerName, 'visibility', 'none');
      }
    };
  }, [map, mapStyle, visibility]);

  return null;
};
